import { orderBy, sumBy, uniqueId, find } from 'lodash';
import { assertNotNull } from '../../../helpers/nullHelpers';
import {
  DesignManifoldDetailsResponse,
  mapDesignManifoldDetailsResponseToFormModel,
} from '../../designs/designManifoldDetailsResponse';
import { BuildTypeCode } from '../../projects/project';
import {
  DataForQuoteOrDesignForm,
  getFloorConstructionOrNull,
  PumpFormModel,
} from '../quote-or-design-form/GetDataForQuoteOrDesignForm';
import { getInitialDesignManifoldDetailsFormModel } from '../quote-or-design-form/manifolds/designManifoldDetailsFormModel';
import { ManifoldFormModel, RoomlessManifoldFormModel } from '../quote-or-design-form/manifolds/manifoldFormModel';
import { FloorLevelCode, ThermostatTypeCode } from '../quote-or-design-form/quoteOrDesignFormData';
import { RoomFormModel } from '../quote-or-design-form/rooms/roomFormModel';
import {
  mapRoomlessManifoldDetailsResponseToRoomFormModel,
  mapRoomResponseToFormModel,
  RoomResponse,
} from '../rooms/roomResponse';
import { UnitResponse } from '../units/unitResponse';

export type ManifoldResponse = {
  manifoldId: number;
  name: string;
  displayIndex: number;
  isRadiator: boolean;
  pumpPartId: number | null;
  designManifoldDetails: DesignManifoldDetailsResponse | null;
  roomlessManifoldDetails: RoomlessManifoldDetailsResponse | null;
  rooms: Array<RoomResponse> | null;
};

export const mapManifoldResponseToFormModel = ({
  manifoldResponse,
  referenceData,
  mapRoomlessManifoldDetailsToRoom,
  createDesignDetails,
  unitResponse,
  buildTypeCode,
}: {
  manifoldResponse: ManifoldResponse;
  referenceData: DataForQuoteOrDesignForm;
  mapRoomlessManifoldDetailsToRoom: boolean;
  createDesignDetails: boolean;
  unitResponse: UnitResponse;
  buildTypeCode: BuildTypeCode;
}): ManifoldFormModel => ({
  manifoldId: manifoldResponse.manifoldId,
  id: uniqueId('manifold'),
  isExpanded: true,
  name: manifoldResponse.name,
  isRadiator: manifoldResponse.isRadiator,
  roomlessManifold:
    mapRoomlessManifoldDetailsToRoom || manifoldResponse.roomlessManifoldDetails == null
      ? null
      : mapRoomlessManifoldDetailsResponseToFormModel(manifoldResponse.roomlessManifoldDetails, referenceData),
  rooms:
    manifoldResponse.rooms == null
      ? manifoldResponse.roomlessManifoldDetails != null && mapRoomlessManifoldDetailsToRoom
        ? [
            mapRoomlessManifoldDetailsResponseToRoomFormModel(
              manifoldResponse.roomlessManifoldDetails,
              referenceData,
              buildTypeCode,
            ),
          ]
        : null
      : getRoomFormModels(manifoldResponse, referenceData, createDesignDetails, buildTypeCode),
  totalUnderfloorHeatingAreaInMetresSquared:
    getTotalUnderfloorHeatingAreaForManifoldInMetresSquared(manifoldResponse, referenceData)?.toString() ?? '',
  totalNumberOfPorts: getTotalNumberOfPortsForManifold(manifoldResponse).toString(),
  skirtingHeatingValidation: null,
  pump: manifoldResponse.pumpPartId !== null ? getManifoldPumpOrNull(referenceData, manifoldResponse.pumpPartId) : null,
  designManifoldDetails:
    manifoldResponse.designManifoldDetails == null
      ? createDesignDetails
        ? getInitialDesignManifoldDetailsFormModel(unitResponse.heatSourceTypeCode, buildTypeCode)
        : null
      : mapDesignManifoldDetailsResponseToFormModel(manifoldResponse.designManifoldDetails),
});

export const getTotalUnderfloorHeatingAreaForManifoldInMetresSquared = (
  manifoldResponse: ManifoldResponse,
  parts: DataForQuoteOrDesignForm,
) => {
  if (manifoldResponse.isRadiator) {
    return 0;
  } else if (manifoldResponse.roomlessManifoldDetails != null) {
    const floorConstruction = getFloorConstructionOrNull(
      manifoldResponse.roomlessManifoldDetails.floorConstructionPartId,
      parts,
    );
    if (floorConstruction != null && floorConstruction.isElectricUnderfloorHeating) {
      return 0;
    }
    return manifoldResponse.roomlessManifoldDetails.areaInMetresSquared;
  }

  return sumBy(
    assertNotNull(manifoldResponse.rooms).filter(room => {
      const floorConstruction = getFloorConstructionOrNull(room.floorConstructionPartId, parts);
      return (
        floorConstruction != null &&
        !floorConstruction.isElectricUnderfloorHeating &&
        !floorConstruction.isSkirtingHeating
      );
    }),
    room => room.areaInMetresSquared ?? 0,
  );
};

export const getTotalNumberOfPortsForManifold = (manifoldResponse: ManifoldResponse): number =>
  manifoldResponse.roomlessManifoldDetails != null
    ? manifoldResponse.roomlessManifoldDetails.numberOfPorts ?? 0
    : sumBy(assertNotNull(manifoldResponse.rooms), room => room.numberOfPorts ?? 0);

const getManifoldPumpOrNull = (
  parts: DataForQuoteOrDesignForm,
  manifoldPumpPartId: number | null,
): PumpFormModel | null => {
  const manifoldPumpPart = find(parts.manifoldPumpOptions, option => option.value?.partId === manifoldPumpPartId);
  return manifoldPumpPart == null ? null : manifoldPumpPart.value;
};

export type RoomlessManifoldDetailsResponse = {
  floorLevelCode: FloorLevelCode;
  floorConstructionPartId: number | null;
  areaInMetresSquared: number | null;
  numberOfPorts: number | null;
  thermostatTypeCode: ThermostatTypeCode;
  numberOfThermostats: number | null;
  numberOfWetSensors: number | null;
  numberOfFloorSensors: number | null;
};

export const mapRoomlessManifoldDetailsResponseToFormModel = (
  roomlessManifoldDetailsResponse: RoomlessManifoldDetailsResponse,
  parts: DataForQuoteOrDesignForm,
): RoomlessManifoldFormModel => ({
  floorLevelCode: roomlessManifoldDetailsResponse.floorLevelCode,
  floorConstruction: getFloorConstructionOrNull(roomlessManifoldDetailsResponse.floorConstructionPartId, parts),
  areaInMetresSquared: roomlessManifoldDetailsResponse.areaInMetresSquared?.toString() ?? '',
  numberOfPorts:
    roomlessManifoldDetailsResponse.numberOfPorts == null
      ? ''
      : roomlessManifoldDetailsResponse.numberOfPorts.toString(),
  thermostatTypeCode: roomlessManifoldDetailsResponse.thermostatTypeCode,
  numberOfThermostats: roomlessManifoldDetailsResponse.numberOfThermostats?.toString() ?? '',
  numberOfWetSensors: roomlessManifoldDetailsResponse.numberOfWetSensors?.toString() ?? '',
  numberOfFloorSensors: roomlessManifoldDetailsResponse.numberOfFloorSensors?.toString() ?? '',
});

type RoomDictionary = {
  [id: number]: RoomFormModel;
};

const getRoomFormModels = (
  manifoldResponse: ManifoldResponse,
  referenceData: DataForQuoteOrDesignForm,
  createDesignDetails: boolean,
  buildTypeCode: 'NewBuild' | 'Retrofit',
): Array<RoomFormModel> => {
  const roomResponses = orderBy(manifoldResponse.rooms, room => room.displayIndex);
  const roomFormModels = roomResponses.map(roomResponse =>
    mapRoomResponseToFormModel(roomResponse, referenceData, createDesignDetails, buildTypeCode),
  );

  return updateSharedThermostatIds(roomResponses, roomFormModels);
};

const updateSharedThermostatIds = (roomResponses: Array<RoomResponse>, roomFormModels: Array<RoomFormModel>) => {
  const formModelsByRoomId: RoomDictionary = Object.fromEntries(
    roomFormModels.map(formModel => [formModel.roomId, formModel]),
  );

  for (const { roomId, thermostatSharedWithRoomId } of roomResponses) {
    const formModel = formModelsByRoomId[roomId];

    if (thermostatSharedWithRoomId) {
      const thermostatSharedWithRoomFormModel = formModelsByRoomId[thermostatSharedWithRoomId];

      formModel.thermostatSharedWithId = thermostatSharedWithRoomFormModel.id;
    }
  }

  return roomFormModels;
};
